import app from 'flarum/forum/app';

/**
 * Shared by the Markets page and the company pages: loading the data (preloaded in the page the first time,
 * then from the API at most every 5 minutes), and how figures are written.
 */
let cache = null;
let cacheAt = 0;
let pending = null;
export function loadMarkets() {
  if (app.data && app.data.vouchaMarkets) {
    cache = app.data.vouchaMarkets;
    cacheAt = Date.now();
    delete app.data.vouchaMarkets;
  }
  if (cache && Date.now() - cacheAt < 5 * 60e3) return Promise.resolve(cache);
  if (pending) return pending;
  pending = app.request({
    method: 'GET',
    url: app.forum.attribute('apiUrl') + '/voucha-markets/data'
  }).then(r => {
    cache = r && r.success ? r.markets : null;
    cacheAt = Date.now();
    pending = null;
    return cache;
  }).catch(e => {
    pending = null;
    throw e;
  });
  return pending;
}

/** A stock's daily closes: preloaded on its own page, otherwise from the API */
export function loadHistory(symbol) {
  const pre = app.data && app.data.vouchaMarketsHistory;
  if (pre && String(pre.symbol).toUpperCase() === String(symbol).toUpperCase()) {
    delete app.data.vouchaMarketsHistory;
    return Promise.resolve(pre.prices || []);
  }
  return app.request({
    method: 'GET',
    url: app.forum.attribute('apiUrl') + '/voucha-markets/history/' + encodeURIComponent(symbol.toLowerCase()),
    errorHandler: () => {}
  }).then(r => r && r.prices || []).catch(() => []);
}

/** Listed stocks with a price, largest first */
export function stocksOf(markets) {
  const all = markets && markets.stocks && markets.stocks.data && markets.stocks.data.stocks || [];
  return all.filter(s => s && s.symbol && Number(s.current_price) > 0).sort((a, b) => (b.market_cap || 0) - (a.market_cap || 0));
}
export function marketStatus(markets) {
  const s = markets && markets.market_status && markets.market_status.data && markets.market_status.data.status;
  return s ? String(s) : null;
}
export function ratesOf(markets) {
  return markets && markets.exchange_rates && markets.exchange_rates.data && markets.exchange_rates.data.rates || {};
}

/** Yesterday's rates, for the day's change */
export function prevRatesOf(markets) {
  return markets && markets.exchange_rates && markets.exchange_rates.data && markets.exchange_rates.data.prev || {};
}
export function stockHref(symbol) {
  return app.route('voucha.markets.stock', {
    symbol: String(symbol).toLowerCase()
  });
}
export function naira(v, compact) {
  if (v === null || v === undefined || v === '' || isNaN(Number(v))) return '—';
  const n = Number(v);
  if (compact) {
    if (n >= 1e12) return '₦' + (n / 1e12).toFixed(2) + 'trn';
    if (n >= 1e9) return '₦' + (n / 1e9).toFixed(2) + 'bn';
    if (n >= 1e6) return '₦' + (n / 1e6).toFixed(2) + 'm';
  }
  return '₦' + n.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });
}
export function pct(v) {
  if (v === null || v === undefined || isNaN(Number(v))) return '—';
  const n = Number(v);
  return (n > 0 ? '+' : '') + n.toFixed(2) + '%';
}
export function tone(v) {
  const n = Number(v);
  if (v === null || v === undefined || isNaN(n) || Math.abs(n) < 0.005) return '';
  return n > 0 ? 'is-up' : 'is-down';
}

/** "Updated 12 minutes ago" (under a day), otherwise "Updated 21 Oct, 22:28", in the reader's own time */
export function updatedText(ts) {
  if (!ts) return 'Not yet updated';
  const d = new Date(String(ts).replace(' ', 'T'));
  if (isNaN(d.getTime())) return 'Not yet updated';
  const mins = Math.round((Date.now() - d.getTime()) / 60000);
  if (mins >= 0 && mins < 2) return 'Updated just now';
  if (mins >= 0 && mins < 60) return 'Updated ' + mins + ' minutes ago';
  if (mins >= 0 && mins < 24 * 60) {
    const h = Math.round(mins / 60);
    return 'Updated ' + h + ' hour' + (h === 1 ? '' : 's') + ' ago';
  }
  return 'Updated ' + d.toLocaleString('en-GB', {
    day: 'numeric',
    month: 'short',
    hour: 'numeric',
    minute: '2-digit'
  });
}
export function ordinal(n) {
  const s = n % 100 >= 11 && n % 100 <= 13 ? 'th' : ['th', 'st', 'nd', 'rd'][n % 10] || 'th';
  return n + s;
}

/** Sectors: how many companies, their combined value, and the day's change weighted by value */
export function sectorsOf(stocks) {
  const by = {};
  stocks.forEach(s => {
    const key = sectorLabel(s.sector);
    const g = by[key] = by[key] || {
      name: key,
      count: 0,
      cap: 0,
      weighted: 0,
      weight: 0
    };
    g.count++;
    g.cap += Number(s.market_cap) || 0;
    if (s.change_percent !== null && s.change_percent !== undefined && Number(s.market_cap) > 0) {
      g.weighted += Number(s.change_percent) * Number(s.market_cap);
      g.weight += Number(s.market_cap);
    }
  });
  const total = Object.values(by).reduce((t, g) => t + g.cap, 0) || 1;
  return Object.values(by).map(g => ({
    name: g.name,
    count: g.count,
    cap: g.cap,
    share: g.cap / total,
    change: g.weight ? g.weighted / g.weight : null
  })).sort((a, b) => b.cap - a.cap);
}

/** "INDUSTRIAL GOODS" -> "Industrial goods", "OIL AND GAS" -> "Oil and gas"; short acronyms (ICT) stay */
export function sectorLabel(sector) {
  const s = String(sector || '').trim();
  if (!s) return 'Other';
  if (s !== s.toUpperCase() || s.length <= 4) return s;
  const lower = s.toLowerCase();
  return lower.charAt(0).toUpperCase() + lower.slice(1);
}

/** A headline figure's value in its unit: 15.39%, $45.01bn, ₦1,328.02, ₦166.79trn */
export function kpiValue(v, unit) {
  const n = d => Number(v).toLocaleString('en-US', {
    maximumFractionDigits: d
  });
  if (unit === '%') return n(2) + '%';
  if (unit === 'US$ bn') return '$' + n(2) + 'bn';
  if (unit === '₦ trn') return '₦' + n(2) + 'trn';
  if (unit === '₦') return '₦' + n(2);
  return n(2) + (unit ? ' ' + unit : '');
}

/** "2026-08" -> "Aug 2026"; "2026-Q2" -> "Q2 2026"; "2026-09-22" -> "22 Sep 2026" */
export function kpiPeriod(p) {
  if (/^\d{4}-Q[1-4]$/.test(p || '')) return p.slice(5) + ' ' + p.slice(0, 4);
  const [y, mo, d] = String(p || '').split('-').map(Number);
  if (!y || !mo) return p || '';
  return new Date(Date.UTC(y, mo - 1, d || 1)).toLocaleDateString('en-GB', {
    ...(d ? {
      day: 'numeric'
    } : {}),
    month: 'short',
    year: 'numeric',
    timeZone: 'UTC'
  });
}